import React, { useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../services/api'
import Header from '../components/Header'
import { FileUploadArea, ClientTypeModal, ImagesModal } from '../components/quotation'
import { colors, borderRadius } from '../styles/tokens'
import { styles } from '../styles/newQuotation.styles'
import { useImageManager } from '../hooks/useImageManager'
import { useAuth } from '../hooks/useAuth'

export default function NewQuotation() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const contactIdFromChat = searchParams.get('contact_id')
  const { profile } = useAuth()
  const fileInputRef = useRef(null)

  const [clientName, setClientName] = useState('')
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)

  const [step, setStep] = useState('upload')
  const [extractedData, setExtractedData] = useState(null)
  const [isReturningClient, setIsReturningClient] = useState(false)

  const [imageUrl1, setImageUrl1] = useState('')
  const [imageUrl2, setImageUrl2] = useState('')
  const [existingImages, setExistingImages] = useState({ img1: null, img2: null })

  const {
    hotelImages,
    carImages,
    tourImages,
    handleImageChange,
    loadImagesForBlocks,
    saveAllImages,
    validateAllImages
  } = useImageManager()

  const isFormValid = clientName.trim() !== '' && files.length > 0

  const handleFilesChange = (newFiles) => {
    setFiles((prev) => [...prev, ...newFiles])
  }

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleExtract = async () => {
    if (!isFormValid) return
    setUploading(true)
    try {
      const result = await api.extractQuote(files)
      setExtractedData(result)
      setStep('client_type')
    } catch (err) {
      alert('Erro ao processar arquivo(s): ' + (err?.message || 'erro desconhecido'))
    } finally {
      setUploading(false)
    }
  }

  const handleClientType = async (returning) => {
    setIsReturningClient(returning)
    setUploading(true)

    try {
      const destination = extractedData?.destination || ''
      const destImages = await api.getDestinationImages(destination)

      const hasImg1 = !!(destImages?.destination_image_url?.trim())
      const hasImg2 = !!(destImages?.destination_image_url_2?.trim())

      setExistingImages({
        img1: hasImg1 ? destImages.destination_image_url : null,
        img2: hasImg2 ? destImages.destination_image_url_2 : null
      })

      if (hasImg1) setImageUrl1(destImages.destination_image_url)
      if (hasImg2) setImageUrl2(destImages.destination_image_url_2)

      const loadedImages = await loadImagesForBlocks(extractedData?.blocks || [])

      const needsDestinationImages = !hasImg1 || !hasImg2
      const allBlockImagesValid = validateAllImages(
        loadedImages.hotels,
        loadedImages.cars,
        loadedImages.tours
      )

      if (!needsDestinationImages && allBlockImagesValid) {
        await createQuote(
          destImages.destination_image_url,
          destImages.destination_image_url_2,
          returning,
          loadedImages.hotels,
          loadedImages.cars,
          loadedImages.tours
        )
        return
      }

      setStep('images')
    } catch (err) {
      alert('Erro ao carregar imagens: ' + (err?.message || 'erro desconhecido'))
    } finally {
      setUploading(false)
    }
  }

  const validateImages = () => {
    if (!String(imageUrl1 || '').trim() || !String(imageUrl2 || '').trim()) return false
    return validateAllImages()
  }

  const handleImages = async () => {
    if (!validateImages()) {
      alert('Preencha todas as URLs de imagem')
      return
    }

    setUploading(true)
    try {
      await saveAllImages()

      // Salva imagens do destino
      await api.saveDestinationImages(extractedData?.destination, imageUrl1, imageUrl2)

      await createQuote(imageUrl1, imageUrl2, isReturningClient, hotelImages, carImages, tourImages)
    } catch (err) {
      alert('Erro ao criar cotação: ' + (err?.message || 'erro desconhecido'))
      setUploading(false)
    }
  }

  const createQuote = async (img1, img2, returning, hotelImgs, carImgs, tourImgs) => {
    // Fail Fast: não permite criar sem seller_id
    if (!profile?.seller_id) {
      alert('Perfil não carregado. Faça login novamente.')
      setUploading(false)
      return
    }

    const blocks = extractedData?.blocks || []

    const blocksWithImages = blocks.map((block) => {
      const imgs =
        block.type === 'hotel' ? hotelImgs?.[block.title] :
          block.type === 'car' ? carImgs?.[block.title] :
            block.type === 'tour' ? tourImgs?.[block.title] :
              null

      if (imgs) {
        return {
          ...block,
          image_url: imgs.img1,
          details: {
            ...(block.details || {}),
            image_url_2: imgs.img2,
            image_url_3: imgs.img3
          }
        }
      }

      return block
    })

    // Calcula start_date/end_date dos blocos se não vier do extractedData
    const blockDates = blocksWithImages
      .map(b => b.date)
      .filter(Boolean)
      .sort()
    const startDate = extractedData?.start_date || blockDates[0] || new Date().toISOString().split('T')[0]
    const endDate = extractedData?.end_date || blockDates[blockDates.length - 1] || startDate

    const quote = await api.createQuoteFromExtracted({
      client_name: clientName,
      destination: extractedData?.destination,
      destination_image_url: img1,
      destination_image_url_2: img2,
      start_date: startDate,
      end_date: endDate,
      blocks: blocksWithImages,
      total_price: extractedData?.total_price,
      seller_id: profile.seller_id,
      is_returning_client: returning,
      contact_id: contactIdFromChat || undefined
    })

    navigate(`/app/cotacao/${quote.id}`)
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.background,
      fontFamily: "'Inter', sans-serif"
    }}>
      <Header subtitle="Nova cotação" userName={profile?.name || 'Usuário'} />

      <main style={{ maxWidth: '768px', margin: '0 auto', padding: '3rem 1.5rem' }}>
        <div style={{
          backgroundColor: colors.card,
          borderRadius: borderRadius.lg,
          border: `1px solid ${colors.border}`,
          padding: '2.5rem'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: colors.primary,
            marginBottom: '2rem'
          }}>
            Nova cotação
          </h2>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: colors.text.primary,
              marginBottom: '0.5rem'
            }}>
              Nome do cliente
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Ex: João da Silva"
              disabled={step !== 'upload'}
              style={{
                ...styles.input,
                opacity: step !== 'upload' ? 0.6 : 1
              }}
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: colors.text.primary,
              marginBottom: '0.5rem'
            }}>
              Arquivo da cotação
            </label>
            <FileUploadArea
              files={files}
              onFilesChange={handleFilesChange}
              onRemoveFile={handleRemoveFile}
              fileInputRef={fileInputRef}
              disabled={step !== 'upload'}
            />
          </div>

          {step === 'upload' && (
            <div style={{
              borderTop: `1px solid ${colors.border}`,
              paddingTop: '1.5rem',
              display: 'flex',
              gap: '1rem'
            }}>
              <button
                onClick={handleExtract}
                disabled={!isFormValid || uploading}
                style={styles.buttonPrimary(isFormValid && !uploading)}
              >
                {uploading ? '⏳ Processando...' : 'Criar cotação'}
              </button>
              <button
                onClick={() => navigate('/app')}
                style={styles.buttonOutline}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      </main>

      {step === 'client_type' && (
        <ClientTypeModal
          destination={extractedData?.destination}
          onSelect={handleClientType}
          onBack={() => setStep('upload')}
          loading={uploading}
        />
      )}

      {step === 'images' && (
        <ImagesModal
          destination={extractedData?.destination}
          imageUrl1={imageUrl1}
          imageUrl2={imageUrl2}
          setImageUrl1={setImageUrl1}
          setImageUrl2={setImageUrl2}
          existingImages={existingImages}
          hotelImages={hotelImages}
          carImages={carImages}
          tourImages={tourImages}
          onImageChange={handleImageChange}
          onSubmit={handleImages}
          onBack={() => setStep('client_type')}
          loading={uploading}
          isValid={validateImages()}
        />
      )}
    </div>
  )
}
